/**
 * PolicyPrefillSelector — below-form HINTS for the in-force-policy resolution
 * the server attaches to `prefill_values` (prefill-policy-lookup AC-6).
 *
 * The dropdown this component used to render for >=1 qualifying policy is
 * SUPERSEDED (operator decision 2026-07-21): the Policy Number schema field
 * itself is now a forced-selection combobox (PolicyNumberCombobox, injected
 * via JsonSchemaForm fieldOverrides). What remains here:
 *
 *   - `policy_options` present        → nothing (combobox owns the field).
 *   - single number, no options (legacy) → subtle "resolved" hint.
 *   - `no_inforce_homeowners_policy`  → "none found" hint.
 */
import { useMemo } from "react";
import type { PolicyInForce } from "~/common/workflows/types/client";
import { toPolicyOptions, readPolicyOptions } from "./policyPrefillOptions";

export interface PolicyPrefillSelectorProps {
  /** Current launch form values (controlled by PreLaunchBody). */
  values: Record<string, unknown>;
  /** Form-value setter — used to write policy_number + policy_type. */
  setValues: (next: Record<string, unknown>) => void;
  /**
   * Called with the chosen policy number after a dropdown pick so the parent
   * can resolve carrier_name (re-trigger prefill with the number as
   * already-extracted; the server's existing carrier lookup fills carrier_name).
   */
  onPolicyChosen?: (policyNumber: string) => void;
  disabled?: boolean;
}

const HINT_RESOLVED = "Resolved from in-force homeowners policy";
const HINT_NONE = "No in-force homeowners policy found — enter or search manually";

export function PolicyPrefillSelector({
  values,
}: PolicyPrefillSelectorProps): JSX.Element | null {
  const policyOptions: PolicyInForce[] = useMemo(
    () => readPolicyOptions(values),
    [values],
  );
  const opts = useMemo(() => toPolicyOptions(policyOptions), [policyOptions]);

  const resolution = typeof values.policy_number_resolution === "string"
    ? (values.policy_number_resolution as string)
    : null;
  const currentNumber = typeof values.policy_number === "string"
    ? (values.policy_number as string)
    : "";

  // >=1 qualifying in-force policy: the Policy Number field ITSELF is now a
  // forced-selection combobox (PolicyNumberCombobox, injected into the schema
  // form via fieldOverrides) — nothing to render below the form.
  if (opts.length >= 1) {
    return null;
  }

  // SINGLE (legacy) — number prefilled but no options emitted. Subtle hint.
  // Rare now that the server always emits policy_options for >=1 qualifying.
  if (currentNumber && resolution === "inforce_homeowners_single") {
    return (
      <p
        className="text-2xs text-primary-gray-light-500"
        data-testid="policy-prefill-selector-single"
      >
        {HINT_RESOLVED}
      </p>
    );
  }

  // ZERO — no qualifying policy (AC-6). Hint only; the schema field stays empty.
  if (!currentNumber && resolution === "no_inforce_homeowners_policy") {
    return (
      <p
        className="text-2xs text-primary-gray-light-500"
        data-testid="policy-prefill-selector-none"
      >
        {HINT_NONE}
      </p>
    );
  }

  // No resolution marker and no options — nothing to render (e.g. the email
  // already carried a policy number, or this isn't a policy workflow).
  return null;
}

export default PolicyPrefillSelector;
